import { RegisterOptions } from 'react-hook-form';
import Select, { Options, OptionProps, components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import style from '../SelectFieldCommons/SelectField.module.scss';
import { SelectCustomStyles } from '../SelectFieldCommons/CustomStyles';
import { getCommonTheme } from '../SelectFieldCommons/CommonTheme';
import formStyles from '../Form/Form.module.scss';
import uncontrolledSelectFieldStyles from './UncontrolledSelectField.module.scss';
import { ReactNode } from 'react';

type UncontrolledSelectFieldProps<T> = {
  name: string;
  placeholder?: string;
  options?: Options<T>;
  value?: T | null;
  onChange?: (selectedOption: T | null, isNew: boolean, meta?: { action: string }) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  defaultValue?: any;
  error?: string;
  rules?: Partial<RegisterOptions>;
  allowCreate?: boolean;
  required?: boolean;
  removeIcon?: ReactNode; // Customizable remove icon
};

const UncontrolledSelectField = ({
  name,
  placeholder,
  options,
  value,
  onChange,
  isDisabled,
  isLoading,
  allowCreate = false,
  error,
  required,
  removeIcon,
}: UncontrolledSelectFieldProps<any>) => {
  const SelectComponent = allowCreate ? CreatableSelect : Select;

  const RemovableOption = (props: OptionProps<any>) => {
    const { data, innerRef, innerProps } = props;
    return (
      <div
        ref={innerRef}
        {...innerProps}
        className={uncontrolledSelectFieldStyles.removable_option}
      >
        <span>{data.label}</span>
        {!data.__isNew__ && (
          <span
            className={uncontrolledSelectFieldStyles.removable_option__icon}
            onClick={(e) => {
              e.stopPropagation(); // Prevents triggering the main option selection
              onChange?.(data, false, { action: 'clear-value' });
            }}
          >
            {removeIcon ?? '×'}
          </span>
        )}
      </div>
    );
  };

  const NoOptionsMessage = (props: any) => {
    return (
      <components.NoOptionsMessage {...props}>
        <span>Type your domain</span>
      </components.NoOptionsMessage>
    );
  };

  return (
    <>
      <SelectComponent
        name={name}
        classNames={{
          dropdownIndicator: () => {
            return style.custom_dropdown_indicator;
          },
        }}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={(selectedOption: any, { action }: any) => {
          if (onChange) onChange(selectedOption, selectedOption?.__isNew__, { action });
        }}
        isDisabled={isDisabled}
        isLoading={isLoading}
        styles={SelectCustomStyles}
        theme={getCommonTheme}
        components={{ Option: RemovableOption, NoOptionsMessage }}
        createOptionPosition="first"
        onCreateOption={(inputValue: string) => {
          onChange?.({ value: inputValue, label: inputValue }, true);
        }}
        required={required}
        inputId={name}
      />
      <div
        className={formStyles.form__input__error__message}
        role="alert"
      >
        {error}
      </div>
    </>
  );
};

export default UncontrolledSelectField;
